<script setup>
import { isEmpty } from 'lodash-es';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useBIFilterMaps } from '~/bi/composables/useBIFilterMaps';
import { OPERATORS_CONFIG_MAP } from '~/bi/constants/bi-constants';
import { getOperatorValue } from '~/bi/utils/bi-filter-helper.utils';

// --------------------------------- Imports -------------------------------- //

// ---------------------------------- Props --------------------------------- //
const props = defineProps({
  selectedColumnConfig: {
    type: Object,
    required: true,
  },
  filterConfig: {
    type: Object,
    default: () => ({}),
  },
  showClearButton: {
    type: Boolean,
    default: false,
  },
  getValuesFromExistingFilters: {
    type: Function,
  },
});
// ---------------------------------- Emits --------------------------------- //
const emit = defineEmits(['apply', 'back', 'cancel', 'clear']);

// ---------------------------- Injects/Provides ---------------------------- //

// ----------------------- Variables - Pinia - consts ----------------------- //
const { getTypeOperatorMap, getOperatorComponent } = useBIFilterMaps();
const auth_store = useAuthStore();

// --------------------- Variables - Pinia - storeToRefs -------------------- //

// ------------------- Variables - Local - consts and lets ------------------ //
const type_operator_map = getTypeOperatorMap();

const available_operators = computed(() => (type_operator_map[props.selectedColumnConfig?.resource_type] || type_operator_map[props.selectedColumnConfig.type]).map(operator_name => OPERATORS_CONFIG_MAP[operator_name]));

// ------------------------ Variables - Local - refs ------------------------ //
const form$ = ref(null);
const filter_config_prop = ref({ ...props.filterConfig });
const selected_operator = ref(filter_config_prop?.value?.operator || (available_operators.value[0] || null));

// ---------------------- Variables - Local - reactives --------------------- //

// --------------------------- Computed properties -------------------------- //
const current_component = computed(() => {
  return getOperatorComponent({
    selected_operator: selected_operator.value,
    selected_column_config: props.selectedColumnConfig,
  });
});
// -------------------------------- Functions ------------------------------- //
function getFormattedOperatorValue(data) {
  const user_timezone = auth_store?.logged_in_user_details?.timezone;
  return getOperatorValue(data, selected_operator.value, props.selectedColumnConfig.type, user_timezone);
}

async function onApply() {
  await form$.value.validate();
  if (form$.value.invalid)
    return;
  // Parsing filters to simplify the structure while sending the data to BE
  const filter_config = {
    field: props.selectedColumnConfig.field,
    op: form$.value.data.operator_name || selected_operator.value.name, // operator_name is used for contains/icontains/relative_date etc cases
    value: getFormattedOperatorValue(form$.value.data),
    meta: {
      is_stage_field: props.selectedColumnConfig.is_stage_field,
      filter_config: {
        operator_value: getFormattedOperatorValue(form$.value.data),
        ...form$.value.data,
      },
    },
  };
  emit('apply', filter_config);
};
function onCancel() {
  emit('cancel');
};
function onClear() {
  emit('clear');
}

function onSelectOperator(operator, close = () => {}) {
  // Reset the filter config if the selected operator is different from the current one to avoid invalid value prefill for saved filters
  if (selected_operator.value.name !== operator.name && !isEmpty(props.filterConfig)) {
    filter_config_prop.value = {};
  }
  selected_operator.value = operator;
  close?.();
};
// -------------------------------- Watchers -------------------------------- //
</script>

<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between">
      <HawkMenu
        :key="selectedColumnConfig.type"
        additional_trigger_classes="!ring-0 !focus:ring-0 !px-2 !py-0"
        :items="available_operators"
        additional_dropdown_classes="!mb-0"
        position="fixed"
      >
        <template #trigger="{ open }">
          <div class="px-3 pt-3 pb-2 w-full flex items-center flex-1 gap-2 text-sm">
            {{ selected_operator.label }}
            <div>
              <IconHawkChevronUp v-if="open" />
              <IconHawkChevronDown v-else />
            </div>
          </div>
        </template>
        <template #content="{ close: closeOperatorMenu }">
          <div class="bg-white p-1 rounded-lg max-h-60 scrollbar">
            <div
              v-for="operator in available_operators" :key="operator.name"
              class="p-2 text-sm font-medium min-w-[10rem] rounded-lg cursor-pointer text-gray-700 hover:bg-gray-50 flex items-center  mt-2"
              @click="onSelectOperator(operator, closeOperatorMenu)"
            >
              {{ operator.label }}
              <IconHawkCheck v-if="selected_operator.name === operator.name" class="ml-auto text-primary-500 w-5 h-5 min-w-5" />
            </div>
          </div>
        </template>
      </HawkMenu>
    </div>
    <!-- Operator config -->
    <div class="mb-2 max-h-96">
      <Vueform ref="form$" size="sm" class="px-2" :display-errors="false">
        <component
          :is="current_component.component"
          :key="selected_operator.name"
          class="col-span-12 p-2 pt-0"
          v-bind="{
            componentConfig: { ...current_component.componentConfig, ...(props.selectedColumnConfig.single_select && { single_select: props.selectedColumnConfig.single_select }) },
            columnConfig: current_component.columnConfig,
            filterConfig: filter_config_prop,
            operatorConfig: current_component.operatorConfig,
            formData: form$?.data,
            formElements: form$?.elements$,
            getValuesFromExistingFilters: props.getValuesFromExistingFilters,
          }"
        />
        <!-- Footer -->
        <div class="col-span-12">
          <hr>
          <div class="flex justify-between mt-2">
            <div class="flex items-center text-sm" :class="{ invisible: !showClearButton }">
              <div class="text-gray-600 cursor-pointer p-2 font-semibold" @click="onClear">
                {{ $t('Clear') }}
              </div>
            </div>
            <div class="flex items-center justify-end text-sm font-semibold">
              <div class="flex items-center">
                <div class="text-gray-600 cursor-pointer p-2" @click="onCancel">
                  {{ $t('Cancel') }}
                </div>
              </div>
              <HawkButton type="link" class="font-semibold !p-2" :disabled="form$?.invalid" @click="onApply">
                {{ $t('Apply') }}
              </HawkButton>
            </div>
          </div>
        </div>
      </Vueform>
    </div>
  </div>
</template>
